import React, { useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import articleService from '../../services/articleService';

interface RichTextEditorProps {
  value: string;
  onChange: (content: string) => void;
  height?: number;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  height = 500,
  placeholder = '記事の内容を入力してください...',
}) => {
  const editorRef = useRef<any>(null);

  // 画像アップロード処理
  const handleImageUpload = async (
    blobInfo: any,
    progress: (percent: number) => void
  ): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      try {
        // プログレス開始
        progress(0);

        // ファイルをバックエンドにアップロード
        const file = blobInfo.blob();
        const result = await articleService.uploadImage(file);

        // プログレス完了
        progress(100);

        // アップロードされた画像のURLを返す
        resolve(result.url);
      } catch (error) {
        reject(error);
      }
    });
  };

  return (
    <Editor
      apiKey={process.env.REACT_APP_TINYMCE_API_KEY || 'no-api-key'}
      onInit={(evt: any, editor: any) => (editorRef.current = editor)}
      value={value}
      onEditorChange={(content: string) => onChange(content)}
      init={{
        height: height,
        menubar: true,
        plugins: [
          'advlist',
          'autolink',
          'lists',
          'link',
          'image',
          'charmap',
          'preview',
          'anchor',
          'searchreplace',
          'visualblocks',
          'code',
          'fullscreen',
          'insertdatetime',
          'media',
          'table',
          'code',
          'help',
          'wordcount',
          'emoticons',
          'autosave',
          'save',
        ],
        toolbar: [
          'undo redo | blocks | bold italic underline strikethrough | fontsize forecolor backcolor',
          'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
          'link image media table | code preview fullscreen | emoticons charmap | save',
        ].join(' | '),
        content_style: `
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif; 
            font-size: 14px;
            line-height: 1.6;
          }
          img {
            max-width: 100%;
            height: auto;
          }
        `,
        placeholder: placeholder,

        // 日本語化
        language: 'ja',
        language_url:
          'https://cdn.jsdelivr.net/npm/tinymce-i18n@23.10.9/langs6/ja.js', // CDN経由で日本語ファイルを読み込み

        // 画像関連の設定
        images_upload_handler: handleImageUpload,
        automatic_uploads: true,
        images_reuse_filename: true,

        // ファイル選択ダイアログの設定
        file_picker_types: 'image',
        file_picker_callback: (callback: any, value: any, meta: any) => {
          if (meta.filetype === 'image') {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');

            input.onchange = function () {
              const file = (this as HTMLInputElement).files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = function () {
                  callback(reader.result as string, { alt: file.name });
                };
                reader.readAsDataURL(file);
              }
            };

            input.click();
          }
        },

        // 追加設定
        branding: false, // TinyMCEのブランディングを非表示
        resize: true, // エディタのリサイズを許可
        autosave_ask_before_unload: true, // ページを離れる前に確認
        autosave_interval: '30s', // 30秒ごとに自動保存

        // テーブル設定
        table_toolbar:
          'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
        table_appearance_options: false,

        // リンク設定
        link_assume_external_targets: true,
        target_list: [
          { title: '同じウィンドウで開く', value: '' },
          { title: '新しいウィンドウで開く', value: '_blank' },
        ],

        // コード設定
        code_dialog_height: 300,
        code_dialog_width: 1000,

        // スタイル設定
        style_formats: [
          {
            title: 'ヘッダー',
            items: [
              { title: 'ヘッダー 1', format: 'h1' },
              { title: 'ヘッダー 2', format: 'h2' },
              { title: 'ヘッダー 3', format: 'h3' },
              { title: 'ヘッダー 4', format: 'h4' },
              { title: 'ヘッダー 5', format: 'h5' },
              { title: 'ヘッダー 6', format: 'h6' },
            ],
          },
          {
            title: 'インライン',
            items: [
              { title: '太字', icon: 'bold', format: 'bold' },
              { title: 'イタリック', icon: 'italic', format: 'italic' },
              { title: '下線', icon: 'underline', format: 'underline' },
              {
                title: '取り消し線',
                icon: 'strikethrough',
                format: 'strikethrough',
              },
              { title: 'コード', icon: 'code', format: 'code' },
            ],
          },
          {
            title: 'ブロック',
            items: [
              { title: '段落', format: 'p' },
              { title: '引用', format: 'blockquote' },
              { title: 'Div', format: 'div' },
              { title: 'Pre', format: 'pre' },
            ],
          },
        ],
      }}
    />
  );
};

export default RichTextEditor;
